import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '../../../../components/Modal';
import { JobCard, Invoice } from '../../config';
import FeesTab from './FeesTab';
import TimeAllocationsTab from './TimeAllocationsTab';
import InvoicesTab from './InvoicesTab';
import OverviewDashboard from './OverviewDashboard';
import GeneralInfoPanel from '../../../../components/GeneralInfoPanel';
import TeamPanel from '../../../../components/TeamPanel';
import TabbedModalLayout, { TabDef } from '../../../../components/TabbedModalLayout';
import PanelHeading from '../../../../components/PanelHeading';
import Skeleton from '../../../../components/Skeleton';
import { TableSkeleton } from '../../../../components/skeletons';
import {
  ClipboardIcon,
  FileTextIcon,
  LayersIcon,
  SlidersIcon,
  UsersIcon,
} from '../../../../components/IconButton/icons';
import { JobCardModalRestore, JobCardModalTab } from './restore';
import './job-card-modal.scss';


interface JobCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobCard: JobCard | null;
  projectId?: number;
  // Optional explicit stage id. Callers from Fees register / Budget supply
  // this directly. Callers that pass a raw `job_cards` view row leave it
  // undefined and we derive (project_id, stage_id) from the JobCard itself.
  stageId?: number | null;
  initialTab?: JobCardModalTab;
}

const EMPTY_JOB_CARD: JobCard = {
  project_id: 0,
  project_name: '',
  job_number: '',
  project_lead_name: '',
  client_name: null,
  allocations: [],
  first_allocation_date: '',
  last_allocation_date: '',
  total_hours: 0,
  total_cost: 0,
  invoices: null,
  total_invoiced: 0,
  invoice_count: 0,
};

const JobCardModal: React.FC<JobCardModalProps> = ({
  isOpen,
  onClose,
  jobCard,
  projectId: projectIdProp,
  stageId: stageIdProp,
  initialTab = 'overview',
}) => {
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<JobCardModalTab>(initialTab);
  const [fetchedJobCard, setFetchedJobCard] = useState<JobCard | null>(null);
  // Hydration state for the placeholder-jobCard callers (Budget, Fees, a
  // restored modal). While it runs the data tabs show skeletons — they used
  // to render the placeholder's empty arrays, i.e. "No invoices found", and
  // then pop. A failure is surfaced rather than left looking like "empty".
  const [hydrating, setHydrating] = useState<boolean>(false);
  const [hydrateError, setHydrateError] = useState<boolean>(false);
  const [hydrateKey, setHydrateKey] = useState(0);

  // FeesTab registers its flush here while mounted; its unmount cleanup (tab
  // switch) parks the fired save's promise in pendingFeeSaveRef. Awaiting
  // both before onClose guarantees callers that refetch on close (Budget,
  // Fees register) see the saved fee, not a cancelled or in-flight save.
  const feesFlushRef = useRef<(() => Promise<void>) | null>(null);
  const pendingFeeSaveRef = useRef<Promise<void> | null>(null);
  // Re-entry guard: while a close-flush is awaited the modal stays
  // interactive, so Escape / click-outside / X could otherwise start a
  // second close chain and double the caller's refetch-on-close.
  const closingRef = useRef(false);

  const handleClose = useCallback(async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      await feesFlushRef.current?.();
      await pendingFeeSaveRef.current;
    } finally {
      closingRef.current = false;
      onClose();
    }
  }, [onClose]);

  // Open an invoice in the builder. The builder lives on its own full-screen
  // route, so this leaves the host page; `returnTo` tells the builder's Close
  // where that was (Budget, Fees or Job Cards, filters included) — without it
  // Close always lands on the invoice register — and `reopenJobCard` lets the
  // host put this modal back on the same tab (see ./restore.ts). Any fee save
  // is settled first for the same reason handleClose does it: the navigation
  // unmounts us.
  const handleOpenInvoice = useCallback(async (
    invoiceId: number,
    reopenJobCard: JobCardModalRestore,
  ) => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      await feesFlushRef.current?.();
      await pendingFeeSaveRef.current;
    } finally {
      // In finally, like handleClose's onClose: a rejected fee save has
      // already reported itself, and must not turn the row click into a
      // dead one.
      closingRef.current = false;
      navigate(`/invoices/${invoiceId}`, {
        state: { returnTo: `${location.pathname}${location.search}`, reopenJobCard },
      });
    }
  }, [navigate, location.pathname, location.search]);

  // Read inside the hydration effect without making it a dependency — the
  // hosts build a fresh placeholder object on every render.
  const jobCardRef = useRef<JobCard | null>(jobCard);
  jobCardRef.current = jobCard;

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  // When the modal is opened with an explicit entity pair (Fees register /
  // Budget callers pass a placeholder jobCard with empty allocations +
  // invoices), hydrate the missing details from the job_cards view so the
  // Time Allocations and Invoices tabs work. Callers from the JobCards page
  // already pass a fully populated jobCard so they skip this fetch.
  useEffect(() => {
    if (!isOpen || projectIdProp == null) {
      setFetchedJobCard(null);
      setHydrating(false);
      setHydrateError(false);
      return;
    }
    let cancelled = false;
    const lookupId = stageIdProp ?? projectIdProp;
    setHydrating(true);
    setHydrateError(false);
    (async () => {
      // Two reads. The view row supplies the time allocations; the invoices
      // come straight from the table for this exact (project, stage).
      //
      // The view can't be trusted for invoices: it overloads project_id with
      // the STAGE's id on stage rows, and projects and stages are numbered
      // independently, so project 15 and stage 15 share one view row whose
      // invoice list is both entities' added together. That showed another
      // job's invoices in this modal — and a click on one opened it. The
      // invoices table has real project_id + stage_id columns, so it is exact.
      // (Allocations keep coming from the view: timesheet_entries has the same
      // single overloaded column, so there is nothing better to ask yet.)
      const invoiceQuery = supabase
        .from('invoices')
        .select(
          'id, invoice_number, invoice_date, recorded_date, our_fee, disbursements, gst_amount, total_amount, status, notes',
        )
        .eq('project_id', projectIdProp);
      const [viewRes, invoiceRes] = await Promise.all([
        supabase
          .from('job_cards_list_view')
          .select('*')
          .eq('project_id', lookupId)
          .maybeSingle(),
        (stageIdProp != null
          ? invoiceQuery.eq('stage_id', stageIdProp)
          : invoiceQuery.is('stage_id', null)
        )
          .order('invoice_date', { ascending: false })
          .order('id', { ascending: false }),
      ]);
      if (cancelled) return;
      setHydrating(false);
      if (viewRes.error || invoiceRes.error) {
        console.error(
          'JobCardModal: failed to hydrate job card',
          viewRes.error ?? invoiceRes.error,
        );
        setHydrateError(true);
        return;
      }
      const invoices = (invoiceRes.data ?? []) as Invoice[];
      setFetchedJobCard({
        // No view row means no time, invoices or fee activity yet; the
        // caller's placeholder already has the right empty shape.
        ...((viewRes.data as JobCard | null) ?? jobCardRef.current ?? EMPTY_JOB_CARD),
        invoices,
        invoice_count: invoices.length,
        total_invoiced: invoices.reduce((sum, inv) => sum + (Number(inv.our_fee) || 0), 0),
      });
    })();
    return () => { cancelled = true; };
  }, [isOpen, projectIdProp, stageIdProp, supabase, hydrateKey]);

  if (!jobCard) return null;

  // Merge: prefer the fetched (full) job card for its data-bearing fields,
  // fall back to the placeholder for the header (project_name, job_number).
  const effectiveJobCard: JobCard = fetchedJobCard
    ? {
        ...fetchedJobCard,
        // Keep the caller's display fields (name + job number may already
        // include the dot-notation stage label they want shown).
        project_name: jobCard.project_name || fetchedJobCard.project_name,
        job_number: jobCard.job_number || fetchedJobCard.job_number,
        // The host knows the entity's real status; the view row may belong
        // to a stage that shares this id (see the hydration effect).
        workflow_status: jobCard.workflow_status ?? fetchedJobCard.workflow_status,
      }
    : jobCard;

  // Derive the unambiguous entity (project_id + stage_id) pair the FeesTab
  // needs. When the caller passes them explicitly (Fees register, Budget) use
  // those. Otherwise prefer the job_cards view's explicit identifiers
  // (parent_project_id + stage_row_id). Fall back to the legacy conflated
  // project_id column only when neither is set — in that legacy case the
  // view emitted the stage's id as project_id with no parent_project_id.
  const hasViewEntityFields =
    effectiveJobCard.parent_project_id != null ||
    effectiveJobCard.stage_row_id != null;
  const feeProjectId =
    projectIdProp ??
    effectiveJobCard.parent_project_id ??
    effectiveJobCard.project_id;
  const feeStageId =
    stageIdProp !== undefined
      ? stageIdProp
      : hasViewEntityFields
        ? (effectiveJobCard.stage_row_id ?? null)
        : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={<strong>{effectiveJobCard.project_name}</strong>}
      size="md"
      hasTabs={true}
      noPadding={true}
      hasFooter={false}
      scrollable={true}
      // The form-ui scope: brings the dropdowns and the General panel onto the
      // 34px/8px look the project modal uses. Modal portals to <body>, so the
      // scope has to sit on the modal's own root to reach its contents.
      className="form-ui"
    >
      <TabbedModalLayout
        tabs={[
          { id: 'overview', label: 'Overview', icon: <ClipboardIcon /> },
          { id: 'project', label: 'Project', icon: <SlidersIcon /> },
          { id: 'fees', label: 'Fees', icon: <LayersIcon /> },
          {
            id: 'timesheets',
            label: hydrating
              ? 'Time Allocations'
              : `Time Allocations (${effectiveJobCard.allocations?.length || 0})`,
            icon: <UsersIcon />,
          },
          {
            id: 'invoices',
            label: hydrating ? 'Invoices' : `Invoices (${effectiveJobCard.invoice_count || 0})`,
            icon: <FileTextIcon />,
          },
        ] as TabDef[]}
        activeTabId={activeTab}
        onTabChange={(id) => setActiveTab(id as JobCardModalTab)}
      >
        <div className="job-card-modal">
          {hydrateError && activeTab !== 'fees' && activeTab !== 'project' && (
            <div className="job-card-modal__load-error" role="alert">
              <span>This job's time and invoices could not be loaded.</span>
              <button
                type="button"
                className="pill-btn pill-btn--sm"
                onClick={() => setHydrateKey((k) => k + 1)}
              >
                Try Again
              </button>
            </div>
          )}

          {activeTab === 'overview' && feeProjectId != null && (
            <OverviewDashboard
              jobCard={effectiveJobCard}
              projectId={feeProjectId}
              stageId={feeStageId ?? null}
              hydrating={hydrating}
              onOpenTab={setActiveTab}
            />
          )}

          {activeTab === 'timesheets' && (hydrating ? (
            <div className="job-card-tab" aria-busy="true">
              <Skeleton variant="rectangular" height={56} borderRadius={8} />
              <TableSkeleton />
            </div>
          ) : (
            <TimeAllocationsTab allocations={effectiveJobCard.allocations ?? []} />
          ))}

          {activeTab === 'invoices' && (hydrating ? (
            <div className="job-card-tab" aria-busy="true">
              <Skeleton variant="rectangular" height={56} borderRadius={8} />
              <TableSkeleton />
            </div>
          ) : (
            <InvoicesTab
              invoices={effectiveJobCard.invoices ?? []}
              onOpenInvoice={(invoiceId) =>
                handleOpenInvoice(invoiceId, {
                  projectId: feeProjectId,
                  stageId: feeStageId ?? null,
                  tab: activeTab,
                  projectName: effectiveJobCard.project_name,
                  jobNumber: effectiveJobCard.job_number,
                  workflowStatus: effectiveJobCard.workflow_status ?? null,
                })
              }
            />
          ))}

          {activeTab === 'fees' && (
            <FeesTab
              projectId={feeProjectId}
              stageId={feeStageId ?? null}
              flushRef={feesFlushRef}
              pendingSaveRef={pendingFeeSaveRef}
            />
          )}

          {activeTab === 'project' && feeProjectId != null && (
            <div className="project-section">
              <PanelHeading title="General" />
              <GeneralInfoPanel projectId={feeProjectId} stageId={feeStageId ?? null} />
              {/* TeamPanel brings its own headings (Lead, Team members); this
                  wrapper only supplies the section boundary above them. */}
              <div className="project-section__team">
                <TeamPanel projectId={feeProjectId} stageId={feeStageId ?? null} />
              </div>
            </div>
          )}
        </div>
      </TabbedModalLayout>
    </Modal>
  );
};

export default JobCardModal;
